import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  Users,
  UserPlus,
  Crown,
  Shield,
  Eye,
  User,
  Copy,
  Link,
  Trash2,
  Settings,
  ChevronRight,
  X,
  Check,
  AlertTriangle,
  Home,
  Mail,
  Clock,
  Edit2,
  RefreshCw
} from 'lucide-react';

// Get backend URL
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8001';
  }
  return process.env.REACT_APP_BACKEND_URL || `https://${hostname}`;
};

const API = `${getBackendUrl()}/api`;

// Role configurations
const ROLES = {
  admin: {
    name: { en: 'Household Admin', es: 'Administrador' },
    description: { en: 'Full access to all features', es: 'Acceso completo a todas las funciones' },
    icon: Crown,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100'
  },
  contributor: {
    name: { en: 'Contributor', es: 'Contribuidor' },
    description: { en: 'Can log expenses and view budgets', es: 'Puede registrar gastos y ver presupuestos' },
    icon: User,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100'
  },
  limited: {
    name: { en: 'Limited Member', es: 'Miembro Limitado' },
    description: { en: 'Personal expenses only (for children)', es: 'Solo gastos personales (para niños)' },
    icon: Shield,
    color: 'text-green-600',
    bgColor: 'bg-green-100'
  },
  viewer: {
    name: { en: 'Viewer', es: 'Observador' },
    description: { en: 'Read-only access', es: 'Acceso de solo lectura' },
    icon: Eye,
    color: 'text-gray-600',
    bgColor: 'bg-gray-100'
  }
};

// Module names for permissions
const MODULES = {
  expense_tracker: { en: 'Expense Tracker', es: 'Gastos' },
  budget: { en: 'Budget', es: 'Presupuesto' },
  net_worth: { en: 'Net Worth', es: 'Patrimonio' },
  financial_kpis: { en: 'Financial KPIs', es: 'KPIs Financieros' },
  forecast: { en: 'Forecast', es: 'Pronóstico' },
  goals: { en: 'Goals', es: 'Metas' },
  savings_capacity: { en: 'Savings Capacity', es: 'Capacidad de Ahorro' },
  wealth_ladder: { en: 'Wealth Ladder', es: 'Escalera de Riqueza' },
  money_cycle: { en: 'Money Cycle', es: 'Ciclo del Dinero' }
};

const FamilyManagementModal = ({ isOpen, onClose, token }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
  
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [household, setHousehold] = useState(null);
  const [membership, setMembership] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [showCreateHousehold, setShowCreateHousehold] = useState(false);
  const [householdName, setHouseholdName] = useState('');
  const [newInviteRole, setNewInviteRole] = useState('contributor');
  const [editingMember, setEditingMember] = useState(null);
  
  // Fetch household data
  const fetchHouseholdData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/household`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setHousehold(data.household);
        setMembership(data.membership);
        setMembers(data.members || []);
        
        if (!data.household) {
          setShowCreateHousehold(true);
        }
      }
    } catch (err) {
      console.error('Error fetching household:', err);
    }
    setLoading(false);
  }, [token]);
  
  // Fetch invites
  const fetchInvites = useCallback(async () => {
    if (!membership?.is_admin) return;
    
    try {
      const response = await fetch(`${API}/household/invites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInvites(data);
      }
    } catch (err) {
      console.error('Error fetching invites:', err);
    }
  }, [token, membership]);
  
  useEffect(() => {
    if (isOpen && token) {
      fetchHouseholdData();
    }
  }, [isOpen, token, fetchHouseholdData]);
  
  useEffect(() => {
    if (membership?.is_admin) {
      fetchInvites();
    }
  }, [membership, fetchInvites]);
  
  // Create household
  const handleCreateHousehold = async () => {
    if (!householdName.trim()) {
      toast.error(lang === 'es' ? 'Ingresa un nombre' : 'Enter a name');
      return;
    }
    
    try {
      const response = await fetch(`${API}/household`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: householdName })
      });
      
      if (response.ok) {
        toast.success(lang === 'es' ? 'Hogar creado exitosamente' : 'Household created successfully');
        setShowCreateHousehold(false);
        fetchHouseholdData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error creating household');
      }
    } catch (err) {
      toast.error('Error creating household');
    }
  };
  
  // Create invite
  const handleCreateInvite = async () => {
    try {
      const response = await fetch(`${API}/household/invite?role=${newInviteRole}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(lang === 'es' ? 'Invitación creada' : 'Invite created');
        fetchInvites();
        
        // Copy to clipboard
        const inviteLink = `${window.location.origin}/join/${data.invite_code}`;
        navigator.clipboard.writeText(inviteLink);
        toast.success(lang === 'es' ? 'Enlace copiado al portapapeles' : 'Link copied to clipboard');
      }
    } catch (err) {
      toast.error('Error creating invite');
    }
  };
  
  // Copy invite link
  const handleCopyInvite = (inviteCode) => {
    const inviteLink = `${window.location.origin}/join/${inviteCode}`;
    navigator.clipboard.writeText(inviteLink);
    toast.success(lang === 'es' ? 'Enlace copiado' : 'Link copied');
  };
  
  // Revoke invite
  const handleRevokeInvite = async (inviteId) => {
    try {
      const response = await fetch(`${API}/household/invite/${inviteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(lang === 'es' ? 'Invitación revocada' : 'Invite revoked');
        fetchInvites();
      }
    } catch (err) {
      toast.error('Error revoking invite');
    }
  };
  
  // Update member role
  const handleUpdateRole = async (memberId, newRole) => {
    try {
      const response = await fetch(`${API}/household/members/${memberId}/role?role=${newRole}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(lang === 'es' ? 'Rol actualizado' : 'Role updated');
        fetchHouseholdData();
        setEditingMember(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error updating role');
      }
    } catch (err) {
      toast.error('Error updating role');
    }
  };
  
  // Remove member
  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(lang === 'es' 
      ? `¿Estás seguro de remover a ${memberName}?`
      : `Are you sure you want to remove ${memberName}?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/household/members/${memberId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(lang === 'es' ? 'Miembro removido' : 'Member removed');
        fetchHouseholdData();
      }
    } catch (err) {
      toast.error('Error removing member');
    }
  };
  
  if (!isOpen) return null;
  
  // Create household screen
  if (showCreateHousehold && !household) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md bg-white">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Home className="w-8 h-8 text-blue-600" />
            </div>
            <CardTitle>{lang === 'es' ? 'Crear tu Hogar Familiar' : 'Create Your Family Household'}</CardTitle>
            <CardDescription>
              {lang === 'es' 
                ? 'Inicia tu cuenta familiar para gestionar finanzas juntos'
                : 'Start your family account to manage finances together'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{lang === 'es' ? 'Nombre del Hogar' : 'Household Name'}</Label>
              <Input
                value={householdName}
                onChange={(e) => setHouseholdName(e.target.value)}
                placeholder={lang === 'es' ? 'Ej: Familia García' : 'E.g., The Garcia Family'}
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose} className="flex-1">
                {lang === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button onClick={handleCreateHousehold} className="flex-1 bg-blue-600 hover:bg-blue-700">
                {lang === 'es' ? 'Crear Hogar' : 'Create Household'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden bg-white flex flex-col">
        {/* Header */}
        <CardHeader className="border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle>{household?.name || (lang === 'es' ? 'Gestión Familiar' : 'Family Management')}</CardTitle>
                <CardDescription>
                  {members.length}/{household?.member_limit || 8} {lang === 'es' ? 'miembros' : 'members'}
                </CardDescription>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            {[
              { id: 'overview', label: { en: 'Overview', es: 'Resumen' }, icon: Home },
              { id: 'members', label: { en: 'Members', es: 'Miembros' }, icon: Users },
              { id: 'invites', label: { en: 'Invites', es: 'Invitaciones' }, icon: UserPlus }
            ].map(tab => (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab(tab.id)}
                className={activeTab === tab.id ? 'bg-blue-600' : ''}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label[lang]}
              </Button>
            ))}
          </div>
        </CardHeader>
        
        <CardContent className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="p-4 text-center">
                        <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-blue-700">{members.length}</p>
                        <p className="text-xs text-blue-600">{lang === 'es' ? 'Miembros' : 'Members'}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="p-4 text-center">
                        <Check className="w-6 h-6 text-green-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-green-700">{household?.member_limit - members.length}</p>
                        <p className="text-xs text-green-600">{lang === 'es' ? 'Disponibles' : 'Available'}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-yellow-50 border-yellow-200">
                      <CardContent className="p-4 text-center">
                        <Link className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-yellow-700">{invites.filter(i => !i.is_expired).length}</p>
                        <p className="text-xs text-yellow-600">{lang === 'es' ? 'Invitaciones' : 'Active Invites'}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-purple-50 border-purple-200">
                      <CardContent className="p-4 text-center">
                        <Crown className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                        <p className="text-sm font-bold text-purple-700">
                          {lang === 'es' ? 'Familia' : 'Family'}
                        </p>
                        <p className="text-xs text-purple-600">$14.99/{lang === 'es' ? 'mes' : 'mo'}</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Member Preview */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      {lang === 'es' ? 'Miembros del Hogar' : 'Household Members'}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {members.map(member => {
                        const roleConfig = ROLES[member.role] || ROLES.viewer;
                        const RoleIcon = roleConfig.icon;
                        return (
                          <div 
                            key={member.id}
                            className={`p-3 rounded-lg border ${roleConfig.bgColor} border-${roleConfig.color.replace('text-', '')}/30`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <RoleIcon className={`w-4 h-4 ${roleConfig.color}`} />
                              <span className="font-medium text-sm truncate">{member.name}</span>
                            </div>
                            <p className="text-xs text-gray-600">{roleConfig.name[lang]}</p>
                            {member.is_owner && (
                              <span className="text-xs bg-yellow-200 text-yellow-800 px-1.5 py-0.5 rounded mt-1 inline-block">
                                {lang === 'es' ? 'Propietario' : 'Owner'}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Quick Actions */}
                  {membership?.is_admin && (
                    <div>
                      <h3 className="font-semibold mb-3">{lang === 'es' ? 'Acciones Rápidas' : 'Quick Actions'}</h3>
                      <div className="flex flex-wrap gap-2">
                        <Button onClick={() => setActiveTab('invites')} variant="outline" size="sm">
                          <UserPlus className="w-4 h-4 mr-2" />
                          {lang === 'es' ? 'Invitar Miembro' : 'Invite Member'}
                        </Button>
                        <Button onClick={() => setActiveTab('members')} variant="outline" size="sm">
                          <Settings className="w-4 h-4 mr-2" />
                          {lang === 'es' ? 'Gestionar Roles' : 'Manage Roles'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Members Tab */}
              {activeTab === 'members' && (
                <div className="space-y-4">
                  {members.map(member => {
                    const roleConfig = ROLES[member.role] || ROLES.viewer;
                    const RoleIcon = roleConfig.icon;
                    const isEditing = editingMember === member.id;
                    
                    return (
                      <div 
                        key={member.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${roleConfig.bgColor}`}>
                            <RoleIcon className={`w-5 h-5 ${roleConfig.color}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{member.name}</p>
                              {member.is_owner && (
                                <span className="text-xs bg-yellow-200 text-yellow-800 px-1.5 py-0.5 rounded">
                                  {lang === 'es' ? 'Propietario' : 'Owner'}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{member.email}</p>
                            <p className="text-xs text-gray-400">{roleConfig.name[lang]}</p>
                          </div>
                        </div>
                        
                        {membership?.is_admin && !member.is_owner && (
                          <div className="flex items-center gap-2">
                            {isEditing ? (
                              <>
                                <Select
                                  value={member.role}
                                  onValueChange={(value) => handleUpdateRole(member.id, value)}
                                >
                                  <SelectTrigger className="w-36">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Object.entries(ROLES).filter(([key]) => key !== 'admin').map(([key, config]) => (
                                      <SelectItem key={key} value={key}>
                                        {config.name[lang]}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => setEditingMember(null)}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => setEditingMember(member.id)}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleRemoveMember(member.id, member.name)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Role Legend */}
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium mb-3">{lang === 'es' ? 'Roles Disponibles' : 'Available Roles'}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Object.entries(ROLES).map(([key, config]) => {
                        const Icon = config.icon;
                        return (
                          <div key={key} className="flex items-start gap-2">
                            <Icon className={`w-4 h-4 mt-0.5 ${config.color}`} />
                            <div>
                              <p className="font-medium text-sm">{config.name[lang]}</p>
                              <p className="text-xs text-gray-500">{config.description[lang]}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Invites Tab */}
              {activeTab === 'invites' && membership?.is_admin && (
                <div className="space-y-6">
                  {/* Create Invite */}
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <UserPlus className="w-4 h-4" />
                        {lang === 'es' ? 'Crear Nueva Invitación' : 'Create New Invite'}
                      </h3>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <div className="flex-1">
                          <Label className="text-sm">{lang === 'es' ? 'Rol del Invitado' : 'Invite Role'}</Label>
                          <Select value={newInviteRole} onValueChange={setNewInviteRole}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(ROLES).filter(([key]) => key !== 'admin').map(([key, config]) => (
                                <SelectItem key={key} value={key}>
                                  {config.name[lang]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button 
                          onClick={handleCreateInvite}
                          className="bg-blue-600 hover:bg-blue-700 self-end"
                          disabled={members.length >= (household?.member_limit || 8)}
                        >
                          <Link className="w-4 h-4 mr-2" />
                          {lang === 'es' ? 'Generar Enlace' : 'Generate Link'}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        {lang === 'es' 
                          ? 'El enlace expirará en 72 horas. Compártelo con el miembro que deseas invitar.'
                          : 'The link will expire in 72 hours. Share it with the member you want to invite.'}
                      </p>
                    </CardContent>
                  </Card>
                  
                  {/* Active Invites */}
                  <div>
                    <h3 className="font-semibold mb-3">
                      {lang === 'es' ? 'Invitaciones Activas' : 'Active Invites'}
                    </h3>
                    {invites.length === 0 ? (
                      <p className="text-gray-500 text-sm">
                        {lang === 'es' ? 'No hay invitaciones activas' : 'No active invites'}
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {invites.map(invite => {
                          const roleConfig = ROLES[invite.role] || ROLES.contributor;
                          return (
                            <div 
                              key={invite.id}
                              className={`flex items-center justify-between p-3 border rounded-lg ${invite.is_expired ? 'bg-gray-100 opacity-60' : ''}`}
                            >
                              <div className="flex items-center gap-3">
                                <Link className="w-5 h-5 text-gray-400" />
                                <div>
                                  <p className="font-mono text-sm">{invite.invite_code}</p>
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span>{roleConfig.name[lang]}</span>
                                    <span>•</span>
                                    <Clock className="w-3 h-3" />
                                    <span>
                                      {invite.is_expired 
                                        ? (lang === 'es' ? 'Expirado' : 'Expired')
                                        : new Date(invite.expires_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCopyInvite(invite.invite_code)}
                                  disabled={invite.is_expired}
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleRevokeInvite(invite.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FamilyManagementModal;
